import React from 'react';

// Presentation only. Deliberately consumes the existing liveTop10 order and
// pre-computed timings; it does not calculate, interpolate or retain telemetry.
const COMPOUNDS = {
  SOFT: { bg: '#e8002d', fg: '#fff', label: 'S' },
  MEDIUM: { bg: '#f9b000', fg: '#111', label: 'M' },
  HARD: { bg: '#e0e0e0', fg: '#111', label: 'H' },
  INTERMEDIATE: { bg: '#22c55e', fg: '#fff', label: 'I' },
  WET: { bg: '#3b82f6', fg: '#fff', label: 'W' },
};
const PODIUM = ['#ffd700', '#c0c0c0', '#cd7f32'];
const F1_PTS = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];
const HEADERS = ['', '#', '', '', 'Driver', 'Pts', 'Tyre', 'Stp', 'Gap', 'Int', 'Lap', 'S1', 'S2', 'S3'];

// Preserve the original timing precision/status representation.
export function formatTimingGap(rawGap) {
  if (rawGap == null) return null;
  return typeof rawGap === 'number' ? `+${rawGap.toFixed(3)}` : String(rawGap).replace(/(\.\d{3})\d+/, '$1');
}
export function formatTimingLap(seconds) {
  if (seconds == null) return null;
  const m = Math.floor(seconds / 60);
  return `${m}:${(seconds - m * 60).toFixed(3).padStart(6, '0')}`;
}

function TyreBadge({ compound, laps }) {
  if (!compound) return <span className="pw2-missing">—</span>;
  const tyre = COMPOUNDS[String(compound).toUpperCase()] || { bg: '#333', fg: '#eee', label: '?' };
  return (
    <span className="pw2-tyre">
      <span className="pw2-tyre-letter" style={{ backgroundColor: tyre.bg, color: tyre.fg }}>{tyre.label}</span>
      {laps ? <span className="pw2-tyre-age">{laps}<small>L</small></span> : null}
    </span>
  );
}

export default function TimingTower({ data, modeLabel = 'LIVE', hidePoints = false }) {
  return (
    <section className="pw2-timing" aria-label="Live timing">
      <header className="pw2-section-header">
        <strong>Live Timing</strong>
        <span className="pw2-live"><i />{modeLabel}</span>
      </header>
      <div className="pw2-timing-header pw2-timing-grid" aria-hidden="true">
        {HEADERS.map((label, index) => <span key={index}>{label}</span>)}
      </div>
      <div className="pw2-timing-rows">
        {data?.liveTop10?.length ? data.liveTop10.map((acr, i) => {
          const teamColour = data.teamColours?.[acr];
          const tc = teamColour ? `#${teamColour}` : '#444';
          const logo = data.teamLogos?.[acr];
          const headshot = data.headshots?.[acr];
          const gap = formatTimingGap(data.gaps?.[acr]);
          const interval = data.intervals?.[acr];
          const lastLap = data.lastLaps?.[acr];
          const sectors = data.lastSectors?.[acr];
          const bestLap = data.sessionBestLap?.acronym === acr;
          return (
            <div key={acr} className={`pw2-timing-row pw2-timing-grid ${i < 3 ? 'pw2-top-three' : ''}`}>
              <span className="pw2-team-stripe" style={{ backgroundColor: tc }} />
              <span className="pw2-position" style={{ color: PODIUM[i] || '#f9b000' }}>{i + 1}</span>
              <span className="pw2-logo" style={{ borderColor: `${tc}88`, backgroundColor: `${tc}22` }}>
                {logo ? <img src={logo} alt="" /> : <span style={{ backgroundColor: tc }} />}
              </span>
              <span className="pw2-portrait" style={{ borderColor: tc, backgroundColor: `${tc}22` }}>
                {headshot ? <img src={headshot} alt="" /> : logo ? <img src={logo} alt="" /> : <span>{acr.slice(0, 1)}</span>}
              </span>
              <span className="pw2-driver" title={data.fullNames?.[acr] || acr}>{acr}</span>
              <span className="pw2-pts">{hidePoints ? '—' : <>{F1_PTS[i]}<small>pt</small></>}</span>
              <span className="pw2-tyre-cell"><TyreBadge compound={data.tyres?.[acr]} laps={data.tyreLaps?.[acr]} /></span>
              <span className="pw2-stops">{data.pitCounts?.[acr] ?? 0}</span>
              <span className={`pw2-gap ${i === 0 ? 'pw2-leader' : ''}`} title={i === 0 ? 'Leader' : gap || ''}>{i === 0 ? 'LEADER' : (gap ?? '—')}</span>
              <span className="pw2-interval">{i === 0 ? '—' : interval != null ? (typeof interval === 'number' ? interval.toFixed(3) : String(interval)) : '—'}</span>
              <span className={`pw2-lap ${bestLap ? 'pw2-best' : ''}`}>{lastLap != null ? formatTimingLap(lastLap) : '—'}</span>
              {[0, 1, 2].map(idx => (
                <span className={`pw2-sector ${data.sessionBestSectors?.[idx]?.acronym === acr ? 'pw2-best' : ''}`} key={idx}>
                  {sectors?.[idx] != null ? sectors[idx].toFixed(1) : '—'}
                </span>
              ))}
            </div>
          );
        }) : <div className="pw2-empty">AWAITING LIVE DATA</div>}
      </div>
    </section>
  );
}
